import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
    BreadcrumbsCommon,
    ClipLoaderCommon,
    MenuAccountComponent,
    StyledBorderButton,
    StyledButton,
    StyledSavedCartPage,
} from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_QUOTES, QuotesBreadcrumbs } from "@routes";
import styled from "styled-components";
import { selectCheckout, useAppDispatch, useAppSelector } from "@redux";
import { LoadingStage } from "src/Interfaces/shared";
import { deleteQuoteAndReload, loadQuotes } from "src/Redux/Slices/quotes";
import { IQuote } from "src/Interfaces/quotes";
import { useNavigate } from "react-router-dom";
import { getItemsPriceInfo } from "src/Module/Checkout/checkoutHelpers";
import { discountApi } from "src/Api/discountApi";
import { fetchActiveDiscount } from "@module";
import { breakpoint } from "@utilities";

export const Quotes = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const quotes = useAppSelector((state) => state.quotes.quotes);
    const quotesLoadingStage = useAppSelector((state) => state.quotes.loadingStage);
    const isLoading = quotesLoadingStage === LoadingStage.IS_LOADING;

    // Local loading state for delete operation
    const [isDeleting, setIsDeleting] = useState(false);

    // Modal state with type tracking and selected quoteId
    const [modalState, setModalState] = useState<{
        isOpen: boolean;
        type?: string;
        quoteId?: string;
    }>({
        isOpen: false,
    });

    useEffect(() => {
        dispatch(loadQuotes());
    }, [dispatch]);

    const closeModal = () => {
        setModalState({ isOpen: false });
    };

    const handleDeleteQuote = () => {
        if (!modalState.quoteId) return;

        try {
            setIsDeleting(true);
            dispatch(deleteQuoteAndReload(modalState.quoteId));
            closeModal();
        } catch (error) {
            console.error("Error deleting quote:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenDetail = (quote: IQuote): void => {
        navigate(`${PATH_QUOTES}/${quote.id}`);
    };

    const QuoteItemContent = ({ quote }: { quote: IQuote }) => {
        const [orderDiscount, setOrderDiscount] = useState<number | undefined>();
        const originalPrice = useMemo(() => {
            return getItemsPriceInfo(quote.items).totalPrice;
        }, [quote.items]);
        const { activeDiscount } = useAppSelector(selectCheckout);
        const [isOrderDiscountLoading, setIsOrderDiscountLoading] = useState(
            !!activeDiscount || (activeDiscount && activeDiscount > 0)
                ? true // if active discount is not available or greater than 0, order discount loading will be triggered below so show loading spinner
                : false,
        );
        const [showHelp, setShowHelp] = useState(false);

        const discountedPrice = useMemo(() => {
            return (orderDiscount ?? 0) > 0
                ? originalPrice - (originalPrice * (orderDiscount ?? 0)) / 100
                : undefined;
        }, [orderDiscount, originalPrice]);

        useEffect(() => {
            if (!activeDiscount && activeDiscount && activeDiscount > 0) {
                dispatch(fetchActiveDiscount());
            }
        }, [activeDiscount]);

        useEffect(() => {
            const fetchDiscount = async () => {
                try {
                    setIsOrderDiscountLoading(true);
                    const response = await discountApi.getQuoteDiscount(quote.id.toString());
                    setOrderDiscount(response.data.data.applicableDiscount);
                } catch (error) {
                    console.error(`Error fetching discount for quote ${quote.id}:`, error);
                } finally {
                    setIsOrderDiscountLoading(false);
                }
            };

            fetchDiscount();
        }, [quote.id]);

        return (
            <>
                <StyledTopSection>
                    <StyledQuoteTitle>{quote.title}</StyledQuoteTitle>
                    <StyledVoucherInfo>
                        {quote.items.length} {quote.items.length === 1 ? "item" : "items"}
                    </StyledVoucherInfo>
                </StyledTopSection>

                <StyledSavedDate>
                    Saved: {moment(quote.created_at).format("MMM DD, YYYY, hh:mm A")}
                </StyledSavedDate>

                <StyledBottomWrapper>
                    {showHelp && (
                        <div className="help-popup-overlay" onClick={() => setShowHelp(false)}>
                            <div
                                className="help-popup-content"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <p>
                                    The final discount shown is based on the items in your cart that
                                    are eligible for the offer. Some products may not qualify, while
                                    others might have reduced discounts due to operator limitations.
                                    That’s why the final discount may be lower than expected — it’s
                                    automatically adjusted based on eligible items only.
                                </p>
                                <StyledButton
                                    $borderRadius="4px"
                                    color={["#436CB2 0%", "#28B7C3 100%"]}
                                    onClick={() => setShowHelp(false)}
                                >
                                    Got it
                                </StyledButton>
                            </div>
                        </div>
                    )}
                    <StyledActionsWrapper>
                        <PriceBlockStyled>
                            <div className="price-label-wrapper">
                                <span className="price-label">Price</span>
                                {(activeDiscount?.discount ?? 0) > 0 && (
                                    <div className="price-discount-badge-wrapper">
                                        {isOrderDiscountLoading ? (
                                            <div className="loading-spinner"></div>
                                        ) : (
                                            (orderDiscount ?? 0) > 0 && (
                                                <>
                                                    <span className="price-discount-badge">
                                                        {orderDiscount}% off
                                                    </span>
                                                    <QuestionMarkHelper>
                                                        <img
                                                            onClick={() => setShowHelp(true)}
                                                            src="/images/HelpIcon.svg"
                                                            alt="info"
                                                        />
                                                    </QuestionMarkHelper>
                                                </>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                            {!isOrderDiscountLoading && (
                                <div className="price-value-wrapper">
                                    <span className="price-discounted-value">
                                        ${(discountedPrice ?? originalPrice).toFixed(2)}
                                    </span>
                                    {/* TODO: show original price if discounted price is available and less than original price */}
                                    {!!discountedPrice && originalPrice > discountedPrice && (
                                        <span className="price-original-value">
                                            ${originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </PriceBlockStyled>
                        <div className="ButtonContainer">
                            <BorderButton
                                onClick={() => handleOpenDetail(quote)}
                                $textColor="#000000"
                                color="transparent"
                                $borderRadius="4px"
                            >
                                Open details
                            </BorderButton>
                        </div>
                    </StyledActionsWrapper>
                </StyledBottomWrapper>
            </>
        );
    };

    return (
        <AppModuleLayout>
            {isLoading ? (
                <ClipLoaderCommon color="#31B4B9" size={70} margin={150} />
            ) : (
                <StyledSavedCartPage>
                    <div className="wrapper">
                        <div className="breadCrumbs">
                            <BreadcrumbsCommon data={QuotesBreadcrumbs} />
                        </div>
                        <div className="subWrapper">
                            <div className="menu">
                                <MenuAccountComponent />
                            </div>
                            <div className="listCarts">
                                <div className="heading">
                                    <p>Quotes</p>
                                </div>
                                {quotes.length > 0 ? (
                                    quotes.map((quote) => (
                                        <StyledSavedCartWrapper key={quote.id}>
                                            <QuoteItemContent quote={quote} />
                                        </StyledSavedCartWrapper>
                                    ))
                                ) : (
                                    <div className="heading">
                                        <p>You don't have any quotes</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Modal directly in the JSX */}
                    {modalState.isOpen && modalState.type === "cancel" && (
                        <ModalOverlay onClick={isDeleting ? undefined : closeModal}>
                            <ModalContainer onClick={(e) => e.stopPropagation()}>
                                <CloseButton
                                    onClick={isDeleting ? undefined : closeModal}
                                    disabled={isDeleting}
                                >
                                    ×
                                </CloseButton>
                                <ModalHeader>
                                    <h2>Cancel quote?</h2>
                                </ModalHeader>
                                <ModalContent>
                                    Are you sure you want to cancel this quote?
                                </ModalContent>
                                {isDeleting ? (
                                    <ClipLoaderCommon color="#31B4B9" size={30} margin={0} />
                                ) : (
                                    <ButtonGroup>
                                        <YesButton
                                            onClick={handleDeleteQuote}
                                            disabled={isDeleting}
                                        >
                                            Yes, Cancel
                                        </YesButton>
                                        <NoButton onClick={closeModal}>No</NoButton>
                                    </ButtonGroup>
                                )}
                            </ModalContainer>
                        </ModalOverlay>
                    )}{" "}
                </StyledSavedCartPage>
            )}
        </AppModuleLayout>
    );
};

/* Styled Components */
const StyledTopSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
`;

const StyledQuoteTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
    color: #333;
`;

const StyledSavedCartWrapper = styled.div`
    background-color: white;
    margin-bottom: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StyledSavedDate = styled.div`
    padding: 0 20px 16px;
    color: #6e7375;
    font-size: 14px;
`;

const StyledBottomWrapper = styled.div`
    padding: 16px 20px;
    border-top: 1px solid #e0e0e0;
    gap: 10px;
    flex-direction: column;
    border-bottom: 1px solid #e0e0e0;
    position: relative;

    .content {
        color: #4f4f4f;
        font-size: 14px;
    }

    .bookingReference {
        color: #131214;
        font-size: 18px;
        font-weight: bold;
    }

    .help-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 5;
    }

    .help-popup-content {
        background: white;
        padding: 2rem;
        border-radius: 8px;
        max-width: 400px;
        ${breakpoint.breakMobile`
            width: 330px;   
        `}
        p {
            text-align: center;
            line-height: 150%;
            font-family: poppins;
        }
        h3 {
            margin-bottom: 1rem;
        }

        button {
            margin-top: 1rem;
            padding: 8px 16px;
        }
    }
`;

const StyledVoucherInfo = styled.div`
    background-color: #e9f8f9;
    padding: 6px 12px;
    border-radius: 50px;
    font-size: 14px;
    color: #31b4b9;
`;

const StyledActionsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    .ButtonContainer {
        display: flex;
        align-items: center;
        gap: 30px;
    }

    .CancelButton {
        font-size: 16px;
        font-weight: 600;
        color: #31b4b9;
        background: none;
        border: none;
        cursor: pointer;
    }
`;

// Modal styled components - updated layout but original colors
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(9, 10, 24, 0.4);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ModalContainer = styled.div`
    background: #fff;
    width: 100%;
    max-width: 400px;
    border-radius: 8px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    position: relative;
    padding: 50px 20px 60px;
    text-align: center;
`;

const ModalHeader = styled.div`
    margin-bottom: 30px;

    h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const ModalContent = styled.div`
    color: #6e7375;
    font-size: 14px;
    margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const YesButton = styled.button`
    width: 50%;
    padding: 12px;
    background: linear-gradient(to right, #436cb2, #28b7c3);
    color: white;
    font-weight: bold;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin: 0 auto;
    &:hover {
        background: linear-gradient(to right, #28b7c3, #436cb2);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

const NoButton = styled.button`
    width: 50%;
    padding: 12px;
    background: transparent;
    color: #31b4b9;
    font-weight: bold;
    border: 1px solid #31b4b9;
    border-radius: 4px;
    cursor: pointer;
    margin: 0 auto;
`;

const CloseButton = styled.button<{ disabled?: boolean }>`
    background: transparent;
    position: absolute;
    top: 15px;
    right: 15px;
    border: none;
    color: ${({ disabled }) => (disabled ? "#ccc" : "#000")}; /* Gray when disabled */
    font-size: 24px;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")}; /* Prevent clicking */
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
`;
const PriceBlockStyled = styled.div`
    font-family: Poppins;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .price-label-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start
        gap: 8px;

        .price-label {
            font-size: 0.875rem;
            color: var(--fg-muted-color);
            font-weight: 300;
            margin-right : 6px;
        }

        .price-discount-badge-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;

            .price-discount-badge {
                font-size: 0.75rem;
                padding: 3px 8px;
                background-color: var(--primary-color);
                color: white;
                border-radius: 16px;
            }

            .loading-spinner {
                width: 16px;
                height: 16px;
                border: 2px solid #f3f3f3;
                border-top: 2px solid var(--primary-color);
                border-radius: 50%;
                animation: spin 1s linear infinite;
            }

            @keyframes spin {
                0% {
                    transform: rotate(0deg);
                }
                100% {
                    transform: rotate(360deg);
                }
            }
        }
    }

    .price-value-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-discounted-value {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--primary-color);
            ${breakpoint.breakMobile`
                font-size : 1.2rem   
            `}
        }

        .price-original-value {
            font-size: 1rem;
            font-weight: 400;
            color: var(--fg-subtle-color);
            text-decoration: line-through;
            ${breakpoint.breakMobile`
                font-size : 0.8rem   
            `}
        }
    }
`;
const QuestionMarkHelper = styled.div`
    cursor: pointer;
`;

const BorderButton = styled(StyledBorderButton)`
    padding: 0px 20px;
    font-size: 16px;
    font-weight: 500;
    border: 1px solid #d9d9d9;
    cursor: pointer;
    margin-top: 0px;
`;
